import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, UserRound, Building2, CalendarClock,
  Activity, Link2, FileAudio, Gauge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageLoader } from '@shared/components/PageLoader';
import { journeyAPI } from '@/utils/supabase/client';
import {
  JOURNEY_STAGE_LABELS,
  JOURNEY_STAGE_ORDER,
  journeyDisplayName,
  phaseForStage,
  type JourneyProspect,
  type JourneyAction,
  type JourneyStage,
} from '@/types/journey';
import { JourneyStageBadge } from './JourneyStageBadge';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';

function Section({ title, icon: Icon, children }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value, icon: Icon }: {
  label: string;
  value: string | null | undefined;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
        <span className="break-all">{value}</span>
      </span>
    </div>
  );
}

function formatDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const ACTION_STATUS_STYLES: Record<string, string> = {
  done: 'bg-success/15 text-success',
  claimed: 'bg-info/15 text-info',
  failed: 'bg-destructive/15 text-destructive',
};

export function JourneyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [journey, setJourney] = useState<JourneyProspect | null>(null);
  const [actions, setActions] = useState<JourneyAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStage, setUpdatingStage] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([journeyAPI.getOne(id), journeyAPI.getActions(id)])
      .then(([j, a]) => { setJourney(j); setActions(a); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleStageChange = async (stage: JourneyStage) => {
    if (!journey) return;
    setUpdatingStage(true);
    try {
      const updated = await journeyAPI.update(journey.id, { stage });
      setJourney(updated);
      toast.success(`Stage updated to ${JOURNEY_STAGE_LABELS[stage]}`);
    } catch {
      toast.error('Failed to update stage');
    } finally {
      setUpdatingStage(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!journey) return (
    <div className="flex items-center justify-center h-full text-muted-foreground">
      Journey not found
    </div>
  );

  const phase = phaseForStage(journey.stage);
  const meeting = formatDateTime(journey.meeting_start_at);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-4 lg:px-6 py-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/journey')} className="shrink-0 mt-0.5">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold font-serif">{journeyDisplayName(journey)}</h1>
                {journey.client_id && (
                  <Badge variant="outline" className="text-xs bg-success/15 text-success">
                    Converted to client
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {journey.company_name && (
                  <span className="text-sm text-muted-foreground">{journey.company_name}</span>
                )}
                {phase && (
                  <Badge variant="outline" className="text-xs">{phase.label} phase</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Stage selector */}
          <div className="flex items-center gap-2 shrink-0">
            <JourneyStageBadge stage={journey.stage} />
            <Select
              value={journey.stage}
              onValueChange={(v) => handleStageChange(v as JourneyStage)}
              disabled={updatingStage}
            >
              <SelectTrigger size="sm" className="w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOURNEY_STAGE_ORDER.map((stage) => (
                  <SelectItem key={stage} value={stage} className="text-xs">
                    {JOURNEY_STAGE_LABELS[stage]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="grid gap-4 lg:grid-cols-2 max-w-5xl">
          <Section title="The intro" icon={Mail}>
            <div className="flex flex-col gap-3">
              <Field label="Prospect" value={journey.prospect_email} icon={Mail} />
              <Field label="Introduced by" value={journey.connector_email} icon={UserRound} />
              <Field label="Received on" value={journey.intro_account} icon={Mail} />
              <Field label="Company" value={journey.company_name} icon={Building2} />
            </div>
          </Section>

          <Section title="Meeting" icon={CalendarClock}>
            {meeting ? (
              <div className="flex flex-col gap-3">
                <Field label="Scheduled" value={meeting} icon={CalendarClock} />
                <Field label="Otter transcript" value={journey.otter_otid} icon={FileAudio} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No meeting booked yet.</p>
            )}
          </Section>

          <Section title="Classification" icon={Gauge}>
            <div className="flex flex-col gap-3">
              {journey.classifier_confidence != null ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-28 shrink-0">Confidence</span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        journey.classifier_confidence >= 0.8 ? 'bg-success'
                          : journey.classifier_confidence >= 0.5 ? 'bg-info'
                          : 'bg-warning'
                      )}
                      style={{ width: `${Math.min(100, journey.classifier_confidence * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono w-10 text-right">
                    {journey.classifier_confidence.toFixed(2)}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not scored.</p>
              )}
              <Field label="Slug" value={journey.slug} icon={Link2} />
              <Field label="Created" value={formatDateTime(journey.created_at)} />
              <Field label="Last updated" value={formatDateTime(journey.updated_at)} />
            </div>
          </Section>

          <Section title="Automation log" icon={Activity}>
            {actions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No actions recorded yet.
              </p>
            ) : (
              <ol className="flex flex-col gap-3">
                {actions.map((a) => (
                  <li key={a.id} className="flex items-start gap-3">
                    <span
                      className={cn(
                        'mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        ACTION_STATUS_STYLES[a.status] ?? 'bg-muted text-muted-foreground'
                      )}
                    >
                      {a.status}
                    </span>
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm break-all">{a.dedupe_key}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(a.created_at) ?? '—'}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Written by the intro-pipeline worker. Read-only here.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
